import PropTypes from 'prop-types';
import classnames from 'classnames';
import formatPrice from '@bwoty-web/price-formatter';
import { connect } from 'react-redux';
import { Picture, Button, WaveRating } from '@bwoty-web/styleguide';
import MatchingBadges from '../matchingBadges/matchingBadges';
import CardLink from '../cardLink/cardLink';
import { getDateFormat } from '../../../utils/date';
import { getCloudinarySources } from '../../../utils/imageUtils';
import { createAnchor, handleAnchor } from '../../../utils/helpers';
import { logProductClickEvent } from '../../../utils/tracking';
import './groupTripCard.scss';

const GroupTripCard = ({
  siteId,
  imageRelativeUrl,
  title,
  subTitle,
  text,
  url,
  tripInfo,
  itemClassifications,
  imageDomain,
  labels,
  trackingProduct,
  filters,
  classifications,
}) => {
  const groupTripCardClasses = classnames(
    'vf-result-card',
    'vf-result-group-trip-card',
  );

  const getDepartureAirports = (departureAirports) => {
    if (departureAirports && Array.isArray(departureAirports)) {
      return departureAirports.map((airport) => airport.text).join(', ');
    }
    return '';
  };

  const getImage = () => {
    const checkedImageRelativeUrl =
      imageRelativeUrl || labels.fallbackGroupTripImage;
    return (
      <Picture
        className="vf-result-group-trip-card__image"
        alt={title}
        sources={[
          ...getCloudinarySources(
            '(max-width: 375px)',
            imageDomain,
            checkedImageRelativeUrl,
            247,
            375,
          ),
          ...getCloudinarySources(
            '(min-width: 376px) and (max-width: 415px)',
            imageDomain,
            checkedImageRelativeUrl,
            272,
            414,
          ),
          ...getCloudinarySources(
            '(min-width: 416px) and (max-width: 767px)',
            imageDomain,
            checkedImageRelativeUrl,
            505,
            767,
          ),
          ...getCloudinarySources(
            '(min-width: 768px) and (max-width: 870px)',
            imageDomain,
            checkedImageRelativeUrl,
            445,
            471,
          ),
          ...getCloudinarySources(
            '(min-width: 871px) and (max-width: 974px)',
            imageDomain,
            checkedImageRelativeUrl,
            390,
            471,
          ),
          ...getCloudinarySources(
            '(min-width: 975px)',
            imageDomain,
            checkedImageRelativeUrl,
            415,
            471,
          ),
        ]}
      />
    );
  };

  const onClick = () => {
    handleAnchor(title);
    logProductClickEvent(trackingProduct);
  };

  return (
    <div id={createAnchor(title)} className={groupTripCardClasses}>
      <div className="vf-result-group-trip-card__container">
        <div className="vf-result-group-trip-card__image-container">
          <CardLink
            anchorName={title}
            url={url}
            trackingProduct={trackingProduct}
          >
            {getImage()}
          </CardLink>
          <MatchingBadges itemClassifications={itemClassifications} filters={filters} classifications={classifications} />
          <CardLink
            anchorName={title}
            url={url}
            trackingProduct={trackingProduct}
          >
            <div className="vf-result-group-trip-card__type">
              {labels.tipTypeGroupTripName}
            </div>
          </CardLink>
        </div>

        <div className="vf-result-group-trip-card__content-container">
          <a
            className="vf-result-group-trip-card__type-content"
            onClick={onClick}
            href={url}
          >
            {labels.tipTypeGroupTripName}
          </a>
          <div className="vf-result-group-trip-card__content-top">
            <a
              className="vf-result-group-trip-card__title"
              onClick={onClick}
              href={url}
            >
              {title}
            </a>
            <div className="vf-result-group-trip-card__subtitle">
              {subTitle}
            </div>
            <div className="vf-result-group-trip-card__description">{text}</div>
          </div>
          <div className="vf-result-group-trip-card__content-middle">
            <CardLink
              anchorName={title}
              url={url}
              trackingProduct={trackingProduct}
            >
              <Picture
                className="vf-result-group-trip-card__hotel-image"
                alt={tripInfo.hotelTitle}
                sources={[
                  ...getCloudinarySources(
                    null,
                    imageDomain,
                    tripInfo.hotelImage,
                    82,
                    100,
                  ),
                ]}
              />
            </CardLink>
            <div className="vf-result-group-trip-card__hotel-container">
              <h3 className="vf-result-group-trip-card__hotel-header">
                {labels.hotelLabel}
              </h3>
              <div className="vf-result-group-trip-card__hotel-name">
                {tripInfo.hotelTitle}
              </div>
              <WaveRating
                className="vf-result-group-trip-card__hotel-rating"
                rating={tripInfo.hotelRating}
                size="small"
              />
            </div>
          </div>
          <div className="vf-result-group-trip-card__content-bottom">
            <div className="vf-result-group-trip-card__bottom-left">
              {`${labels.groupTripFlightAndHotelText} ${getDateFormat(
                tripInfo.departureDate,
                siteId,
                true,
              )} 
              ${labels.groupTripFromText} ${getDepartureAirports(
                tripInfo.departureAirports,
              )}`}
            </div>
            <div className="vf-result-group-trip-card__bottom-vr" />
            <div className="vf-result-group-trip-card__bottom-middle">
              <div className="vf-result-group-trip-card__price">
                <span>{labels.groupTripOnePersonFromText}</span>
                <span className="vf-result-group-trip-card__price-value">
                  {formatPrice(tripInfo.price, siteId, true)}
                </span>
              </div>
            </div>
            <div className="vf-result-group-trip-card__bottom-vr" />
            <div className="vf-result-group-trip-card__mobile-bottom-wrapper">
              <div className="vf-result-group-trip-card__mobile-bottom-left">
                <div>
                  {`${labels.groupTripFlightAndHotelText}, ${labels.groupTripOnePersonText}`}
                </div>
                <div className="vf-result-group-trip-card__mobile-bottom-middle">
                  {getDateFormat(tripInfo.departureDate, siteId, true)}{' '}
                  {labels.groupTripFromText}{' '}
                  {getDepartureAirports(tripInfo.departureAirports)}
                </div>
                <div>
                  <span>{labels.fromLabel}</span>
                  <span className="vf-result-group-trip-card__price-value">
                    {formatPrice(tripInfo.price, siteId, true)}
                  </span>
                </div>
              </div>
            </div>
            <div className="vf-result-group-trip-card__bottom-right">
              <CardLink
                anchorName={title}
                url={url}
                trackingProduct={trackingProduct}
              >
                <Button
                  theme="primary"
                  className="vf-result-group-trip-card__button"
                  href={url}
                >
                  {labels.buttonText}
                </Button>
              </CardLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

GroupTripCard.defaultProps = {
  siteId: null,
};

GroupTripCard.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  imageDomain: PropTypes.string.isRequired,
  imageRelativeUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  itemClassifications: PropTypes.arrayOf(PropTypes.string).isRequired,
  tripInfo: PropTypes.shape({
    hotelImage: PropTypes.string,
    hotelTitle: PropTypes.string,
    hotelRating: PropTypes.number,
    departureDate: PropTypes.string,
    price: PropTypes.string,
    departureAirports: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
      }),
    ),
  }),
  labels: PropTypes.shape({
    tipTypeGroupTripName: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    hotelLabel: PropTypes.string.isRequired,
    fromLabel: PropTypes.string.isRequired,
    fallbackGroupTripImage: PropTypes.string.isRequired,
    groupTripFlightAndHotelText: PropTypes.string.isRequired,
    groupTripFromText: PropTypes.string.isRequired,
    groupTripOnePersonText: PropTypes.string.isRequired,
    groupTripOnePersonFromText: PropTypes.string.isRequired,
  }).isRequired,
  trackingProduct: PropTypes.shape({}).isRequired,
};

GroupTripCard.defaultProps = {
  tripInfo: null,
};

const mapStateToProps = ({ siteSettings }) => ({
  filters: siteSettings.filters,
  classifications: siteSettings.classifications,
});

export default connect(mapStateToProps)(GroupTripCard);
